import { useCallback } from "react";
import { useRouter } from "@tanstack/react-router";
import { QueryClient } from "@/lib/ReactQuery/QueryClient";

/** Minimal org shape needed to switch scope: the id and its tenant prefix. */
type SwitchableOrganisation = { id: string | number; is_admin?: boolean };

export function useOrganisationSwitcher() {
  const router = useRouter();

  const switchOrganisation = useCallback(
    async (org: SwitchableOrganisation) => {
      const orgId = String(org.id);

      // Org scope rides on a request header, not the query key, so the previous
      // org's data would otherwise leak through the cache. Drop every org-scoped
      // query (keeping the user-level `auth` queries) BEFORE navigating, so the
      // app-layout beforeLoad refetches the new org's supplier list and
      // recomputes the supplier (e.g. canonicalising onto a sole supplier).
      // removeQueries (not invalidateQueries) is required: beforeLoad reads via
      // ensureQueryData, which returns cached data even when stale, but refetches
      // when the entry is absent.
      QueryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== "auth",
      });

      // Non-admins land on the bare org URL — the supplier picker, which
      // auto-selects a sole supplier or sends an empty org to onboarding. Admins
      // work across suppliers, so they land on the "all suppliers" dashboard
      // (the optional supplier segment omitted) instead of a picker.
      const tenant = org.is_admin ? "admin" : "org";
      const to = org.is_admin
        ? `/${tenant}/${orgId}/supplier`
        : `/${tenant}/${orgId}`;

      await router.navigate({ to });
    },
    [router],
  );

  return { switchOrganisation };
}
